import { FC, useContext, useEffect, useReducer } from 'react'

import { autoExecuteAsync } from 'utility-tools/module/async'
import { removeAuthOnAnonymousUserFound } from 'nexchange-service/module/auth'
import {
    getPrices,
    getValidPairsAndCurrencies,
    TCurrencyWithVolumeRecord,
    TPriceWindowApiDto,
} from 'nexchange-service/module/coins'
import { ICurrencyDto, IValidatedPairsDto } from 'nexchange-service'

import config from '@/config'
import SEO from '@/components/Layout/MetadataManager'
import Layout from '@/components/Layout/Layout'
import { getServerSideTranslationProps } from '@/utils/translation'
import DataParamsReducer, {
    coinsDataContext,
    dataParamsInitState,
    SetCoinDataAction,
} from '@/features/SupportedAssets/CoinDetailsPage/reducers/exchangeWidget/setCoinTickersToExchangeWidget'
import dynamic from 'next/dynamic'

const { EXCHANGE_WIDGET_CONFIGS } = config
const {
    FROM_AVAILABILITY_MODE: coinTypeAvailabilityModeFrom,
    TO_AVAILABILITY_MODE: coinTypeAvailabilityModeTo,
} = EXCHANGE_WIDGET_CONFIGS.INDEX

// Import setIsIFramed dynamically to avoid top-level await
const getSetIsIFramed = () => import('nexchange-service/module/iframe').then(mod => mod.setIsIFramed)
const HeroSection = dynamic(() => import('../features/LandingPage/HeroSection'), {
    loading: () => <div>Loading...</div>,
})

interface HeroSectionProps {
    availableCurrencies: ICurrencyDto[]
    availablePairs: IValidatedPairsDto
    coinTypeAvailabilityModeFrom: any // We'll use any temporarily since we don't have access to the actual type
    coinTypeAvailabilityModeTo: any // We'll use any temporarily since we don't have access to the actual type
}

const LandingPage: FC<{
    availableCurrencies: ICurrencyDto[]
    currencyWithVolumeRecord: TCurrencyWithVolumeRecord
    availablePairs: IValidatedPairsDto
    prices: TPriceWindowApiDto[]
}> = ({ availableCurrencies, currencyWithVolumeRecord, availablePairs, prices }) => {
    const [exchangeWidgetState, dispatchExchangeWidgetAction] = useReducer(
        DataParamsReducer,
        dataParamsInitState || { tickers: [] },
    )

    useEffect(() => {
        dispatchExchangeWidgetAction(new SetCoinDataAction({ tickers: prices }))
    }, [prices])

    const onMountRender = async () => {
        const setIsIFramed = await getSetIsIFramed()
        setIsIFramed(false)
        await removeAuthOnAnonymousUserFound()
    }

    useEffect(
        () =>
            autoExecuteAsync(async () => {
                await onMountRender()
            }),
        [],
    )

    return (
        <Layout>
            <coinsDataContext.Provider
                value={{ state: exchangeWidgetState, dispatchAction: dispatchExchangeWidgetAction }}
            >
                <SEO
                    title="EasyWallet - Your Secure Cryptocurrency Wallet"
                    description="Secure and user-friendly cryptocurrency wallet supporting BTC, ETH, LTC and popular altcoins. Send, receive, buy and sell crypto with ease. Best-in-class security features and intuitive interface."
                    keywords="cryptocurrency wallet, bitcoin wallet, ethereum wallet, litecoin wallet, crypto exchange, secure wallet, digital assets"
                    type="website"
                    image="https://easywallet.io/images/social-share.jpg"
                />

                <HeroSection
                    availableCurrencies={availableCurrencies}
                    availablePairs={availablePairs}
                    coinTypeAvailabilityModeFrom={coinTypeAvailabilityModeFrom}
                    coinTypeAvailabilityModeTo={coinTypeAvailabilityModeTo}
                />
            </coinsDataContext.Provider>
        </Layout>
    )
}

const i18nNamespaces = ['home', 'partners', 'order']

export const getStaticProps = async ({ req, res, locale, defaultLocale }) => {
    try {
        const { availableCurrencyDtoList, validatedPairsDto, currencyWithVolumeRecord } =
            await getValidPairsAndCurrencies({
                coinTypeAvailabilityModeFrom,
                coinTypeAvailabilityModeTo,
            })

        // // Enable ISR with a revalidation period of 30 seconds
        // res.setHeader('Cache-Control', 'public, s-maxage=30, stale-while-revalidate=59')

        return {
            props: {
                ...(await getServerSideTranslationProps({ locale, i18nNamespaces })),
                availableCurrencies: availableCurrencyDtoList,
                currencyWithVolumeRecord,
                availablePairs: validatedPairsDto,
                prices: {}, // allPrices
            },
        }
    } catch (error) {
        console.error('Error in getServerSideProps:', error)
        return {
            props: {
                ...(await getServerSideTranslationProps({ locale, i18nNamespaces })),
                availableCurrencies: [],
                currencyWithVolumeRecord: {},
                availablePairs: {},
                prices: {},
            },
        }
    }
}

export default LandingPage
